// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, EMPTY, from, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import * as UserContextSlice from "../../user-context/slice"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import { RootState } from "../../../app/store"
import {
  BatchGetTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsResponse,
  TimesheetFieldConfigRef,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type GenerateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest) => Observable<GenerateTimesheetFieldConfigsResponse>

export const generateTimesheetFieldConfigs = (generateTimesheetFieldConfigsRpc: GenerateTimesheetFieldConfigsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.generateTimesheetFieldConfigs.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        if (!action.payload.length) {
          return EMPTY
        }
        return from(action.payload)
          .pipe(
            mergeMap(timeGroup => {
              const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
              const tagId = timeGroup.tagsList
                .filter(t => t.siteId === currentSiteId)?.[0]?.id ?? ""
              if (!currentSiteId) {
                return EMPTY
              }
              const request = new GenerateTimesheetFieldConfigsRequest()
                .setSiteId(currentSiteId)
                .setTagId(tagId)
                .setSelectedTimesheetFieldOptionIdsList(
                  timeGroup.selectedTimesheetFieldOptionsList
                    .filter(o => o.siteId === currentSiteId)
                    .map(o => o.timesheetFieldOptionId)
                )
              return generateTimesheetFieldConfigsRpc(request).pipe(
                map(t => ({
                  timeGroupId: timeGroup.id,
                  configRefsList: t.toObject().configRefsList,
                })),
                withLatestFrom(state$),
                mergeMap(([payload, currentState]) => {
                  const actions: Action[] = [
                    Slice.setTimeGroupFieldConfigs({
                      timeGroupId: payload.timeGroupId,
                      configHashList: payload.configRefsList.map(configRef => configRef.configHash),
                    }),
                  ]
                  const timesheetFieldConfigsCache = Slice.selectTimesheetFieldConfigsCache(currentState)
                  const pendingTimesheetFieldConfigRequests = Slice.selectPendingTimesheetFieldConfigRequests(currentState)
                  const nonCachedConfigRefsList = payload.configRefsList
                    .filter(configRef => (
                      timesheetFieldConfigsCache[configRef.configHash] === undefined
                      && !pendingTimesheetFieldConfigRequests.includes(configRef.configHash)
                    ))
                  if (nonCachedConfigRefsList.length) {
                    actions.push(
                      Actions.batchGetTimesheetFieldConfigs(
                        new BatchGetTimesheetFieldConfigsRequest()
                          .setSiteId(currentSiteId)
                          .setConfigRefsList(
                            nonCachedConfigRefsList.map(configRef => (
                              new TimesheetFieldConfigRef()
                                .setTimesheetFieldId(configRef.timesheetFieldId)
                                .setConfigHash(configRef.configHash)
                            ))
                          )
                      ),
                    )
                  }
                  return concat(actions)
                }),
                catchError(() => of(showSnackBar({
                  message: t({
                    id: "msg_generate_timesheet_field_configs_failed",
                    message: "Failed to load timesheet field settings. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
                  }),
                  messageType: "ERROR",
                }))),
              )
            }),
          )
      }),
    )

const generateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest): Observable<GenerateTimesheetFieldConfigsResponse> =>
  grpc(() => timesheetFieldClient.generateTimesheetFieldConfigs(request, null))

export default generateTimesheetFieldConfigs(generateTimesheetFieldConfigsRpc)
